import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import ViewSelector from "../components/ViewSelector";
import KanbanView from "../components/KanbanView";
import CardsView from "../components/CardsView";
import ListView from "../components/ListView";
import {
  ClipboardList,
  Loader2,
  Eye,
  Trash2,
  FileText,
  User,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Search,
  Package,
  Calendar,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Palmtree,
  CalendarDays
} from "lucide-react";

const ESTADOS = [
  { value: "pendiente", label: "Pendiente", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300", icon: Clock },
  { value: "en_revision", label: "En Revisión", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300", icon: AlertCircle },
  { value: "aprobada", label: "Aprobada", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300", icon: CheckCircle },
  { value: "rechazada", label: "Rechazada", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300", icon: XCircle },
  { value: "completada", label: "Completada", color: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300", icon: CheckCircle }
];

// KPI Stats component
const StatsCards = ({ solicitudes, activeFilter, onFilterChange }) => {
  const stats = [
    { key: "all", label: "Total", count: solicitudes.length, icon: Package, color: "blue" },
    { key: "pendiente", label: "Pendientes", count: solicitudes.filter(s => s.estado === "pendiente").length, icon: Clock, color: "amber" },
    { key: "en_revision", label: "En Revisión", count: solicitudes.filter(s => s.estado === "en_revision").length, icon: AlertCircle, color: "blue" },
    { key: "aprobada", label: "Aprobadas", count: solicitudes.filter(s => s.estado === "aprobada").length, icon: CheckCircle, color: "green" },
    { key: "rechazada", label: "Rechazadas", count: solicitudes.filter(s => s.estado === "rechazada").length, icon: XCircle, color: "red" },
    { key: "completada", label: "Completadas", count: solicitudes.filter(s => s.estado === "completada").length, icon: CheckCircle, color: "slate" },
  ];

  const colorClasses = {
    blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
    amber: "bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400",
    green: "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400",
    red: "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400",
    slate: "bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400",
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
      {stats.map(stat => {
        const Icon = stat.icon;
        const isActive = activeFilter === stat.key;
        return (
          <Card
            key={stat.key}
            className={`cursor-pointer transition-all hover:shadow-md dark:bg-slate-800 dark:border-slate-700 ${isActive ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => onFilterChange(stat.key)}
            data-testid={`stat-${stat.key}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${colorClasses[stat.color]}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stat.count}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default function SolicitudesGHPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [solicitudes, setSolicitudes] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  // Initialize statusFilter from URL query param or default to "all"
  const [statusFilter, setStatusFilter] = useState(() => {
    const urlEstado = searchParams.get("estado");
    return urlEstado || "all";
  });
  const [plantillaFilter, setPlantillaFilter] = useState("all");
  const [currentView, setCurrentView] = useState("table");
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  
  // Selection state for bulk delete
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  
  // New tabs state
  const [activeMainTab, setActiveMainTab] = useState("solicitudes");
  const [saldos, setSaldos] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loadingSaldos, setLoadingSaldos] = useState(false);
  const [loadingEventos, setLoadingEventos] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [searchSaldos, setSearchSaldos] = useState("");
  const [searchCalendario, setSearchCalendario] = useState("");
  
  const { getAuthHeaders, user } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [solRes, plantRes] = await Promise.all([
        axios.get(`${API}/gestion-humana/solicitudes`, getAuthHeaders()),
        axios.get(`${API}/gestion-humana/plantillas`, getAuthHeaders())
      ]);
      setSolicitudes(solRes.data);
      setPlantillas(plantRes.data);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);
  
  const fetchSaldos = useCallback(async () => {
    try {
      setLoadingSaldos(true);
      const res = await axios.get(`${API}/gestion-humana/saldos-general`, getAuthHeaders());
      setSaldos(res.data.saldos || []);
    } catch (err) {
      console.error("Error fetching saldos:", err);
    } finally {
      setLoadingSaldos(false);
    }
  }, [getAuthHeaders]);
  
  const fetchEventos = useCallback(async () => {
    try {
      setLoadingEventos(true);
      const res = await axios.get(`${API}/gestion-humana/calendario-general`, getAuthHeaders());
      setEventos(res.data.eventos || []);
    } catch (err) {
      console.error("Error fetching eventos:", err);
    } finally {
      setLoadingEventos(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Load saldos and eventos when their respective tabs are active - ALWAYS refresh
  useEffect(() => {
    if (activeMainTab === "saldos") {
      fetchSaldos();
    } else if (activeMainTab === "calendario") {
      fetchEventos();
    }
  }, [activeMainTab, fetchSaldos, fetchEventos]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEstadoInfo = (estado) => ESTADOS.find(e => e.value === estado) || ESTADOS[0];
  
  // Helper to refresh all data including saldos and eventos
  const refreshAllData = useCallback(() => {
    fetchData();
    // Also refresh saldos and eventos to keep them in sync
    fetchSaldos();
    fetchEventos();
  }, [fetchData, fetchSaldos, fetchEventos]);

  const updateEstado = async (solicitudId, estado) => {
    try {
      await axios.put(`${API}/gestion-humana/solicitudes/${solicitudId}/estado?estado=${estado}`, {}, getAuthHeaders());
      toast.success("Estado actualizado");
      refreshAllData(); // Refresh all data including saldos and calendario
      if (selectedSolicitud?.id === solicitudId) {
        setSelectedSolicitud(prev => ({ ...prev, estado }));
      }
    } catch (err) {
      toast.error("Error al actualizar estado");
    }
  };

  const deleteSolicitud = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta solicitud?")) return;
    try {
      await axios.delete(`${API}/gestion-humana/solicitudes/${id}`, getAuthHeaders());
      toast.success("Solicitud eliminada");
      refreshAllData(); // Refresh all data including saldos and calendario
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  // Selection functions for bulk delete
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredSolicitudes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredSolicitudes.map(s => s.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!deletePassword || selectedIds.length === 0) {
      toast.error("Ingrese su contraseña para confirmar");
      return;
    }
    
    setDeleting(true);
    try {
      const res = await axios.post(
        `${API}/gestion-humana/solicitudes/bulk-delete`,
        {
          ids: selectedIds,
          password: deletePassword
        },
        getAuthHeaders()
      );
      
      toast.success(`${res.data.deleted || selectedIds.length} solicitudes eliminadas`);
      setSelectedIds([]);
      setShowBulkDeleteDialog(false);
      setDeletePassword("");
      refreshAllData(); // Refresh all data including saldos and calendario
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al eliminar solicitudes";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  // Filter solicitudes
  let filteredSolicitudes = solicitudes;
  
  if (statusFilter !== "all") {
    filteredSolicitudes = filteredSolicitudes.filter(sol => sol.estado === statusFilter);
  }
  
  if (plantillaFilter !== "all") {
    filteredSolicitudes = filteredSolicitudes.filter(sol => sol.plantilla_id === plantillaFilter || sol.plantilla_nombre === plantillaFilter);
  }
  
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filteredSolicitudes = filteredSolicitudes.filter(sol =>
      sol.plantilla_nombre?.toLowerCase().includes(query) ||
      sol.user_name?.toLowerCase().includes(query) ||
      Object.values(sol.values || {}).some(v => String(v).toLowerCase().includes(query))
    );
  }

  const isAdmin = user?.role?.toLowerCase() === 'administrador' || user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'gestion_humana';
  const isSupervisor = user?.is_supervisor || user?.role?.toLowerCase() === 'supervisor';

  // Prepare data for different views
  const kanbanColumns = ESTADOS.map(e => ({
    id: e.value,
    title: e.label,
    items: filteredSolicitudes.filter(s => s.estado === e.value).map(s => ({
      id: s.id,
      title: s.plantilla_nombre || "Solicitud",
      subtitle: s.user_name || "Usuario",
      badge: formatDate(s.created_at).split(',')[0],
      status: e.value
    }))
  }));

  const cardItems = filteredSolicitudes.map(s => {
    const estado = getEstadoInfo(s.estado);
    return {
      id: s.id,
      title: s.plantilla_nombre || "Solicitud",
      subtitle: s.user_name || "Usuario",
      description: Object.entries(s.values || {}).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(", "),
      badge: estado.label,
      badgeColor: estado.color,
      meta: formatDate(s.created_at)
    };
  });

  const listItems = filteredSolicitudes.map(s => {
    const estado = getEstadoInfo(s.estado);
    return {
      id: s.id,
      title: s.plantilla_nombre || "Solicitud",
      subtitle: `${s.user_name || "Usuario"} - ${formatDate(s.created_at)}`,
      badge: estado.label,
      badgeColor: estado.color
    };
  });

  // Get unique plantilla names for filter
  const uniquePlantillas = [...new Set(solicitudes.map(s => s.plantilla_nombre).filter(Boolean))];
  
  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const days = [];
    // Add empty cells for days before the first day of month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };
  
  const getEventsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    const q = searchCalendario.trim().toLowerCase();
    return eventos.filter(e => {
      const inicio = e.fecha_inicio?.split('T')[0];
      const fin = e.fecha_fin?.split('T')[0];
      const inRange = dateStr >= inicio && dateStr <= fin;
      if (!inRange) return false;
      if (!q) return true;
      return (e.user_name || "").toLowerCase().includes(q);
    });
  };
  
  const getEventColor = (evento) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const inicio = new Date(evento.fecha_inicio);
    inicio.setHours(0, 0, 0, 0);
    
    const diffDays = Math.ceil((inicio - today) / (1000 * 60 * 60 * 24));
    
    if (evento.tipo === "permiso") {
      return "bg-orange-500 text-white"; // Permisos siempre naranja
    }
    
    // Vacaciones
    if (diffDays > 5 || diffDays < -5) {
      return "bg-red-500 text-white"; // Fuera del rango ±5 días
    } else {
      return "bg-yellow-500 text-black"; // Dentro del rango ±5 días
    }
  };
  
  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  const dayNames = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="solicitudes-gh-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
            Solicitudes de Gestión Humana
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Vista de todas las solicitudes realizadas en el sistema
          </p>
        </div>
        
        {/* Main Tabs */}
        <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="solicitudes" className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              Solicitudes
            </TabsTrigger>
            {(isAdmin || isSupervisor) && (
              <TabsTrigger value="saldos" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Saldo General
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="calendario" className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4" />
                Calendario General
              </TabsTrigger>
            )}
          </TabsList>
          
          {/* Tab: Solicitudes (existing content) */}
          <TabsContent value="solicitudes">
            {/* Stats KPI Cards */}
            <StatsCards
              solicitudes={solicitudes}
              activeFilter={statusFilter}
              onFilterClick={(filter) => {
                setStatusFilter(filter);
                if (filter !== "all") {
                  setSearchParams({ estado: filter });
                } else {
                  setSearchParams({});
                }
              }}
            />

        {/* Main Content */}
        <Card className="dark:bg-slate-800 dark:border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por tipo, solicitante..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                  data-testid="search-input"
                />
              </div>
              <Select value={plantillaFilter} onValueChange={setPlantillaFilter}>
                <SelectTrigger className="w-48" data-testid="plantilla-filter">
                  <SelectValue placeholder="Tipo de solicitud" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {uniquePlantillas.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-4">
              {isAdmin && currentView === "table" && filteredSolicitudes.length > 0 && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="select-all-sol"
                    checked={selectedIds.length === filteredSolicitudes.length && filteredSolicitudes.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                  <Label htmlFor="select-all-sol" className="text-sm cursor-pointer">
                    {selectedIds.length === filteredSolicitudes.length ? "Deseleccionar" : "Seleccionar"} todo
                  </Label>
                  {selectedIds.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowBulkDeleteDialog(true)}
                      data-testid="bulk-delete-sol-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar ({selectedIds.length})
                    </Button>
                  )}
                </div>
              )}
              <ViewSelector currentView={currentView} onViewChange={setCurrentView} />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : filteredSolicitudes.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400">No hay solicitudes</p>
              </div>
            ) : currentView === "table" ? (
              <div className="space-y-3">
                {filteredSolicitudes.map(sol => {
                  const estado = getEstadoInfo(sol.estado);
                  const EstadoIcon = estado.icon;
                  const isSelected = selectedIds.includes(sol.id);
                  return (
                    <div
                      key={sol.id}
                      className={`flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:shadow-sm transition-shadow ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                      data-testid={`solicitud-${sol.id}`}
                    >
                      <div className="flex items-center gap-4">
                        {isAdmin && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(sol.id)}
                          />
                        )}
                        <div className={`p-2 rounded-full ${estado.color}`}>
                          <EstadoIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {sol.plantilla_nombre || "Solicitud"}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap text-sm text-slate-500 dark:text-slate-400">
                            <span>{sol.user_name || "Usuario"}</span>
                            {sol.user_id !== user?.id && (
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700">
                                <Users className="w-3 h-3 mr-1" />
                                Supervisado{sol.created_by_name && sol.created_by_name !== sol.user_name ? `: ${sol.created_by_name}` : ''}
                              </Badge>
                            )}
                            <span>- {formatDate(sol.created_at)}</span>
                          </div>
                          {/* Show some values preview */}
                          {sol.values && Object.keys(sol.values).length > 0 && (
                            <p className="text-xs text-slate-400 mt-1 truncate max-w-md">
                              {Object.entries(sol.values).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(" | ")}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={estado.color}>{estado.label}</Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setSelectedSolicitud(sol); setShowViewDialog(true); }}
                          data-testid={`view-solicitud-${sol.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => deleteSolicitud(sol.id)}
                            data-testid={`delete-solicitud-${sol.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : currentView === "kanban" ? (
              <KanbanView
                columns={kanbanColumns}
                onItemClick={(item) => {
                  const sol = solicitudes.find(s => s.id === item.id);
                  if (sol) { setSelectedSolicitud(sol); setShowViewDialog(true); }
                }}
              />
            ) : currentView === "cards" ? (
              <CardsView
                items={cardItems}
                onItemClick={(item) => {
                  const sol = solicitudes.find(s => s.id === item.id);
                  if (sol) { setSelectedSolicitud(sol); setShowViewDialog(true); }
                }}
              />
            ) : (
              <ListView
                items={listItems}
                onItemClick={(item) => {
                  const sol = solicitudes.find(s => s.id === item.id);
                  if (sol) { setSelectedSolicitud(sol); setShowViewDialog(true); }
                }}
              />
            )}
          </CardContent>
        </Card>

        {/* View Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="dark:bg-slate-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle>Detalle de Solicitud</DialogTitle>
              <DialogDescription>
                {selectedSolicitud?.plantilla_nombre}
              </DialogDescription>
            </DialogHeader>
            {selectedSolicitud && (
              <ScrollArea className="max-h-[60vh]">
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-500">Solicitante</Label>
                      <p className="font-medium flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        {selectedSolicitud.user_name || "Usuario"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Fecha</Label>
                      <p className="font-medium flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {formatDate(selectedSolicitud.created_at)}
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-500">Estado</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={getEstadoInfo(selectedSolicitud.estado).color}>
                        {getEstadoInfo(selectedSolicitud.estado).label}
                      </Badge>
                      {isAdmin && (
                        <Select
                          value={selectedSolicitud.estado}
                          onValueChange={(v) => updateEstado(selectedSolicitud.id, v)}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ESTADOS.map(e => (
                              <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>

                  {selectedSolicitud.values && Object.keys(selectedSolicitud.values).length > 0 && (
                    <div className="border-t pt-4">
                      <Label className="text-slate-500 mb-3 block">Datos de la Solicitud</Label>
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(selectedSolicitud.values)
                          .filter(([k]) => !k.startsWith('_') && k !== 'vacacion_solicitud_id')
                          .map(([key, value]) => (
                            <div key={key} className="bg-slate-50 dark:bg-slate-700/50 p-3 rounded-lg">
                              <Label className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</Label>
                              <p className="font-medium text-slate-900 dark:text-white mt-1">{String(value) || "-"}</p>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {selectedSolicitud.comentarios && (
                    <div className="border-t pt-4">
                      <Label className="text-slate-500">Comentarios</Label>
                      <p className="mt-1">{selectedSolicitud.comentarios}</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
          <DialogContent className="max-w-md dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Confirmar Eliminación Masiva
              </DialogTitle>
              <DialogDescription>
                Está a punto de eliminar <strong>{selectedIds.length}</strong> solicitudes de forma permanente.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-300">
                      Esta acción es IRREVERSIBLE
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      Las solicitudes seleccionadas serán eliminadas permanentemente.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="delete-password">Ingrese su contraseña para confirmar</Label>
                <Input
                  id="delete-password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="••••••••"
                  onKeyDown={(e) => e.key === "Enter" && handleBulkDelete()}
                  data-testid="delete-password-input"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowBulkDeleteDialog(false);
                  setDeletePassword("");
                }}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={deleting || !deletePassword}
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar {selectedIds.length} solicitudes
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </TabsContent>
          
          {/* Tab: Saldo General - Admin/GH y Supervisores (solo sus supervisados) */}
          {(isAdmin || isSupervisor) && (
          <TabsContent value="saldos">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Saldo General de Vacaciones y Permisos
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    {isAdmin ? "Vista consolidada de todos los usuarios" : "Vista consolidada de sus supervisados"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar por nombre, email o departamento..."
                      value={searchSaldos}
                      onChange={(e) => setSearchSaldos(e.target.value)}
                      className="pl-10 w-72"
                      data-testid="search-saldos-input"
                    />
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={fetchSaldos}
                    disabled={loadingSaldos}
                  >
                    {loadingSaldos ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingSaldos ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  </div>
                ) : (
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-100 dark:bg-slate-800">
                          <TableHead>Usuario</TableHead>
                          <TableHead>Departamento</TableHead>
                          <TableHead className="text-center">
                            <span className="flex items-center justify-center gap-1">
                              <Palmtree className="w-4 h-4 text-yellow-600" />
                              Vacaciones Pendientes
                            </span>
                          </TableHead>
                          <TableHead className="text-center">
                            <span className="flex items-center justify-center gap-1">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              Vacaciones Tomadas
                            </span>
                          </TableHead>
                          <TableHead className="text-center">
                            <span className="flex items-center justify-center gap-1">
                              <Clock className="w-4 h-4 text-orange-600" />
                              Permisos Pendientes
                            </span>
                          </TableHead>
                          <TableHead className="text-center">
                            <span className="flex items-center justify-center gap-1">
                              <CheckCircle className="w-4 h-4 text-blue-600" />
                              Permisos Tomados
                            </span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {saldos.filter((s) => {
                          if (!searchSaldos.trim()) return true;
                          const q = searchSaldos.toLowerCase();
                          return (s.nombre || "").toLowerCase().includes(q)
                            || (s.email || "").toLowerCase().includes(q)
                            || (s.departamento || "").toLowerCase().includes(q);
                        }).map((s) => (
                          <TableRow key={s.user_id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                                  <User className="w-4 h-4 text-blue-600" />
                                </div>
                                <div>
                                  <p className="font-medium">{s.nombre}</p>
                                  <p className="text-xs text-slate-500">{s.email}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{s.departamento || "Sin asignar"}</Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={s.vacaciones_pendientes > 0 ? "warning" : "secondary"}>
                                {s.vacaciones_pendientes} días
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="success" className="bg-green-100 text-green-700">
                                {s.vacaciones_tomadas} días
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={s.permisos_pendientes > 0 ? "warning" : "secondary"} className="bg-orange-100 text-orange-700">
                                {s.permisos_pendientes}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className="bg-blue-100 text-blue-700">
                                {s.permisos_tomados}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                        {saldos.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                              No hay datos de saldos disponibles
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}
          
          {/* Tab: Calendario General - Solo Admin/GH */}
          {isAdmin && (
          <TabsContent value="calendario">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarDays className="w-5 h-5" />
                    Calendario General de Ausencias
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    Vacaciones y permisos de todos los colaboradores
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="font-medium min-w-[150px] text-center">
                    {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                  </span>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar colaborador..."
                      value={searchCalendario}
                      onChange={(e) => setSearchCalendario(e.target.value)}
                      className="pl-10 w-56"
                      data-testid="search-calendario-input"
                    />
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={fetchEventos}
                    disabled={loadingEventos}
                    className="ml-2"
                  >
                    {loadingEventos ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Legend */}
                <div className="flex flex-wrap gap-4 mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-yellow-500"></div>
                    <span className="text-sm">Vacaciones (±5 días de hoy)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-500"></div>
                    <span className="text-sm">Vacaciones (fuera de rango)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-orange-500"></div>
                    <span className="text-sm">Permisos</span>
                  </div>
                </div>
                
                {loadingEventos ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  </div>
                ) : (
                  <div className="rounded-lg border overflow-hidden">
                    {/* Calendar Header */}
                    <div className="grid grid-cols-7 bg-slate-100 dark:bg-slate-800">
                      {dayNames.map(day => (
                        <div key={day} className="p-2 text-center font-medium text-sm border-b">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7">
                      {getDaysInMonth(calendarMonth).map((day, idx) => {
                        const dayEvents = day ? getEventsForDate(day) : [];
                        const isToday = day && day.toDateString() === new Date().toDateString();
                        
                        return (
                          <div 
                            key={idx} 
                            className={`min-h-[120px] border-b border-r p-1 ${
                              !day ? 'bg-slate-50 dark:bg-slate-900' : ''
                            } ${isToday ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                          >
                            {day && (
                              <>
                                <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600 font-bold' : 'text-slate-600 dark:text-slate-400'}`}>
                                  {day.getDate()}
                                </div>
                                <div className="space-y-1">
                                  {dayEvents.slice(0, 3).map((evento, eIdx) => (
                                    <div 
                                      key={eIdx}
                                      className={`text-xs px-1 py-0.5 rounded truncate ${getEventColor(evento)}`}
                                      title={`${evento.tipo === 'vacacion' ? 'Vacación' : 'Permiso'}: ${evento.user_name}`}
                                    >
                                      {evento.tipo === 'vacacion' ? '🏖️' : '📋'} {evento.user_name?.split(' ')[0]}
                                    </div>
                                  ))}
                                  {dayEvents.length > 3 && (
                                    <div className="text-xs text-slate-500 pl-1">
                                      +{dayEvents.length - 3} más
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
